import qrcode from "qrcode-generator";
import { useQuery } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import { getPayOptions, payOnlineLine, upiPayURL, type PayOptions } from "../../api/payOptions";

/** This seller's ways to pay. A failed read is not "no gateway": see readFailed. */
export function usePayOptions(ns: string, accountId = "") {
  const identity = useIdentity();
  return useQuery<PayOptions>({
    queryKey: ["payoptions", ns, accountId],
    enabled: Boolean(identity.token && ns),
    queryFn: ({ signal }) => getPayOptions(identity, ns, accountId, signal),
    retry: false,
  });
}

/** The line under a greyed Pay online (owner 2026-11-01). */
export function PayOnlineNotice({ options, readFailed, why }: { options?: PayOptions | undefined; readFailed?: boolean | undefined; why?: string | undefined }) {
  const line = payOnlineLine(options, Boolean(readFailed), why ?? "");
  if (!line) return null;
  return <Typography variant="caption" display="block" color={readFailed ? "warning.main" : "text.secondary"} data-testid="pay-online-line">{line}</Typography>;
}

function qrDataURL(text: string): string {
  const qr = qrcode(0, "M");
  qr.addData(text);
  qr.make();
  return qr.createDataURL(5, 2);
}

/**
 * The QR for a UPI ID: no gateway and no fee, so nothing tells us it was paid.
 * The transaction number is still recorded, and the seller checks the bank.
 */
export function UpiPayQR({ options, amount, note }: { options?: PayOptions | undefined; amount: number | null; note: string }) {
  if (!options) return null;
  const upi = options.upi;
  if (!upi) {
    return <Alert severity="info" sx={{ py: 0 }}>No UPI ID is set up, so there is no QR. Pay by UPI and enter the transaction number.</Alert>;
  }
  return (
    <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
      <img src={qrDataURL(upiPayURL(upi.upi_id, upi.label, amount, note))} alt={`UPI QR for ${upi.upi_id}`} width={160} height={160} />
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>Pay to <b>{upi.upi_id}</b>{amount != null && amount > 0 ? <> · ₹{amount.toFixed(2)}</> : null}</Typography>
        <Typography variant="caption" color="text.secondary">
          UPI does not tell us when this is paid. Check the money arrived and that the amount matches, then enter the transaction number.
        </Typography>
      </Box>
    </Box>
  );
}
